import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  CheckCircle2,
  ChevronRight,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const LIFESTYLE_QUESTIONS = [
  {
    id: 'diet_type',
    question: 'What is your primary diet?',
    type: 'select',
    options: ['Raw', 'Vegan', 'Vegetarian', 'Non-Vegetarian']
  },
  {
    id: 'exercise_frequency',
    question: 'How often do you exercise?',
    type: 'select',
    options: ['Never', '1-2 times/week', '3-4 times/week', '5-6 times/week', 'Daily']
  },
  {
    id: 'digestion_quality',
    question: 'How would you describe your digestion?',
    type: 'radio',
    options: ['Excellent', 'Good', 'Average', 'Poor']
  },
  {
    id: 'energy_levels',
    question: 'How are your energy levels throughout the day?',
    type: 'radio',
    options: ['Consistently high', 'Variable', 'Low in afternoon', 'Generally low']
  },
  {
    id: 'body_type',
    question: 'How would you describe your body type?',
    type: 'radio',
    options: ['Thin/Light', 'Medium/Athletic', 'Heavy/Stocky']
  }
];

export const Questions = () => {
  const navigate = useNavigate();
  const { id: consultationId } = useParams();
  const [searchParams] = useSearchParams();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState('lifestyle'); // 'lifestyle' or 'clarifying'
  const [lifestyleAnswers, setLifestyleAnswers] = useState({});
  const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
  const [clarifyingAnswers, setClarifyingAnswers] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  useEffect(() => {
    // Check payment status if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      fetchConsultation();
    }
  }, [consultationId, searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 5;
      
      while (attempts < maxAttempts) {
        const response = await api.get(`/api/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful!', { id: 'payment-success' });
          break;
        }
        
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      fetchConsultation();
    } catch (error) {
      console.error('Error verifying payment:', error);
      fetchConsultation();
    }
  };

  const fetchConsultation = async () => {
    try {
      const response = await api.get(`/api/consultations/${consultationId}`);
      setConsultation(response.data);
      
      // Check if payment is pending
      if (response.data.payment_status === 'pending') {
        toast.error('Payment required');
        navigate(`/consultation/${consultationId}/payment`);
        return;
      }
      
      // Check if already has diagnosis
      if (response.data.status === 'diagnosed' || response.data.status === 'completed') {
        navigate(`/consultation/${consultationId}/diagnosis`);
        return;
      }
      
      // Check if lifestyle answers already submitted
      if (response.data.lifestyle_answers) {
        setLifestyleAnswers(response.data.lifestyle_answers);
        setCurrentStep('clarifying');
        generateClarifyingQuestions();
      }
      
    } catch (error) {
      console.error('Error fetching consultation:', error);
      toast.error('Failed to load consultation');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLifestyleChange = (questionId, value) => {
    setLifestyleAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const submitLifestyleAnswers = async () => {
    // Validate all required questions are answered
    const unanswered = LIFESTYLE_QUESTIONS.filter(q => !lifestyleAnswers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. Missing: ${unanswered[0].question}`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/api/consultations/${consultationId}/lifestyle`, lifestyleAnswers);
      toast.success('Lifestyle information saved', { id: 'lifestyle-saved' });
      setCurrentStep('clarifying');
      generateClarifyingQuestions();
    } catch (error) {
      console.error('Error saving lifestyle answers:', error);
      toast.error('Failed to save answers');
    } finally {
      setSubmitting(false);
    }
  };

  const generateClarifyingQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const response = await api.post(`/api/consultations/${consultationId}/generate-questions`, {});
      setClarifyingQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error generating questions:', error);
      // Use default questions on error
      setClarifyingQuestions([
        { id: 'q1', question: 'How would you describe your body type - thin/medium/heavy build?' },
        { id: 'q2', question: 'Do you notice the symptoms worsen at any particular time of day?' },
        { id: 'q3', question: 'How is your appetite and digestion generally?' },
        { id: 'q4', question: 'Do you experience any coating on your tongue in the morning?' },
        { id: 'q5', question: 'Are there any foods that seem to aggravate your symptoms?' }
      ]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleClarifyingChange = (questionId, value) => {
    setClarifyingAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const submitClarifyingAnswers = async () => {
    // Validate all questions are answered
    const unanswered = clarifyingQuestions.filter(q => !clarifyingAnswers[q.id]);
    if (unanswered.length > 0) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/api/consultations/${consultationId}/clarifying`, { answers: clarifyingAnswers });
      toast.success('All answers submitted', { id: 'answers-submitted' });
      navigate(`/consultation/${consultationId}/diagnosis`);
    } catch (error) {
      console.error('Error saving clarifying answers:', error);
      toast.error('Failed to save answers');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="questions-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-stone-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {currentStep === 'lifestyle' ? 'Lifestyle Questionnaire' : 'Clarifying Questions'}
            </h1>
            <p className="text-sm text-stone-500">Step 3 of 4: Help us understand you better</p>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center gap-2 mb-8">
          {['Concerns', 'Payment', 'Questions', 'Assessment'].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className={`step-indicator ${index < 2 ? 'completed' : index === 2 ? 'active' : 'pending'}`}>
                {index < 2 ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
              </div>
              <span className={`ml-2 text-sm ${index === 2 ? 'text-[hsl(22,70%,44%)] font-medium' : index < 2 ? 'text-[hsl(135,15%,33%)]' : 'text-stone-400'}`}>
                {step}
              </span>
              {index < 3 && <div className={`w-8 h-px mx-4 ${index < 2 ? 'bg-[hsl(135,15%,33%)]' : 'bg-stone-300'}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 pb-12">
        <div className="consultation-card">
          {currentStep === 'lifestyle' ? (
            <>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-[hsl(22,70%,44%)/0.1] rounded-2xl flex items-center justify-center">
                  <MessageCircle className="w-7 h-7 text-[hsl(22,70%,44%)]" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Lifestyle Assessment
                  </h2>
                  <p className="text-stone-600">Understanding your daily habits helps us provide better recommendations</p>
                </div>
              </div>

              <div className="space-y-6">
                {LIFESTYLE_QUESTIONS.map((q, index) => (
                  <div key={q.id} className="bg-stone-50 rounded-xl p-6" data-testid={`lifestyle-question-${q.id}`}>
                    <Label className="text-[hsl(120,10%,18%)] font-medium mb-3 block">
                      {index + 1}. {q.question}
                    </Label>
                    
                    {q.type === 'select' ? (
                      <Select
                        value={lifestyleAnswers[q.id] || ''}
                        onValueChange={(value) => handleLifestyleChange(q.id, value)}
                      >
                        <SelectTrigger className="w-full" data-testid={`select-${q.id}`}>
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {q.options.map(option => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <RadioGroup
                        value={lifestyleAnswers[q.id] || ''}
                        onValueChange={(value) => handleLifestyleChange(q.id, value)}
                        className="flex flex-wrap gap-4"
                      >
                        {q.options.map(option => (
                          <div key={option} className="flex items-center space-x-2">
                            <RadioGroupItem value={option} id={`${q.id}-${option}`} />
                            <Label htmlFor={`${q.id}-${option}`} className="text-stone-600 cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                  </div>
                ))}
              </div>

              <Button
                onClick={submitLifestyleAnswers}
                disabled={submitting}
                className="w-full btn-primary text-lg py-6 mt-8"
                data-testid="continue-lifestyle-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-[hsl(22,70%,44%)/0.1] rounded-2xl flex items-center justify-center">
                  <MessageCircle className="w-7 h-7 text-[hsl(22,70%,44%)]" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Clarifying Questions
                  </h2>
                  <p className="text-stone-600">Based on your symptoms, we have a few more questions</p>
                </div>
              </div>

              {loadingQuestions ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-stone-600">Generating personalized questions...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {clarifyingQuestions.map((q, index) => (
                    <div key={q.id} className="bg-stone-50 rounded-xl p-6" data-testid={`clarifying-question-${q.id}`}>
                      <Label className="text-[hsl(120,10%,18%)] font-medium mb-3 block">
                        {index + 1}. {q.question}
                      </Label>
                      <Textarea
                        value={clarifyingAnswers[q.id] || ''}
                        onChange={(e) => handleClarifyingChange(q.id, e.target.value)}
                        placeholder="Your answer..."
                        className="min-h-[100px]"
                        data-testid={`answer-${q.id}`}
                      />
                    </div>
                  ))}

                  <Button
                    onClick={submitClarifyingAnswers}
                    disabled={submitting}
                    className="w-full btn-primary text-lg py-6 mt-8"
                    data-testid="get-diagnosis-btn"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Get My Assessment
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};
